import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Spinner, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import API_BASE_URL from "../../config";

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem("selectedCompany");
    return storedCompany ? JSON.parse(storedCompany).id : "";
  });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    price_cents: "",
    branch_id: () => {
      const storedBranch = localStorage.getItem("selectedBranch");
      return storedBranch ? JSON.parse(storedBranch).id : "";
    },
    included_services: [],
  });
  const [loading, setLoading] = useState(false);

  // 🔹 Load companies and validate selectedCompany
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const companyData = res.data || [];
        setCompanies(companyData);

        // Validate stored selectedCompany
        const storedCompany = localStorage.getItem("selectedCompany");
        if (storedCompany) {
          const parsedCompany = JSON.parse(storedCompany);
          const isValidCompany = companyData.some((c) => c.id === parsedCompany.id);
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompany");
            localStorage.removeItem("selectedBranch");
            setSelectedCompany("");
            setForm((prev) => ({ ...prev, branch_id: "" }));
          } else {
            setSelectedCompany(parsedCompany.id);
            fetchData(parsedCompany.id);
          }
        }
      } catch (err) {
        console.error("fetchCompanies", err);
        Swal.fire("Error", "Failed to load companies", "error");
        setSelectedCompany("");
        setForm((prev) => ({ ...prev, branch_id: "" }));
      }
    }
    fetchCompanies();
  }, []);

  // 🔹 Load branches, services, and packages when selectedCompany changes
  useEffect(() => {
    if (!selectedCompany) {
      setBranches([]);
      setServices([]);
      setPackages([]);
      setForm((prev) => ({ ...prev, branch_id: "" }));
      return;
    }

    async function fetchBranches() {
      try {
        const res = await axios.get(`${API_BASE_URL}/companies/${selectedCompany}/branches`);
        const branchData = res.data || [];
        setBranches(branchData);

        // Validate stored selectedBranch
        const storedBranch = localStorage.getItem("selectedBranch");
        if (storedBranch) {
          const parsedBranch = JSON.parse(storedBranch);
          const isValidBranch = branchData.some((b) => b.id === parsedBranch.id);
          if (!isValidBranch) {
            localStorage.removeItem("selectedBranch");
            setForm((prev) => ({ ...prev, branch_id: "" }));
          } else {
            setForm((prev) => ({ ...prev, branch_id: parsedBranch.id }));
          }
        }
      } catch (err) {
        console.error("fetchBranches", err);
        Swal.fire("Error", "Failed to load branches", "error");
        setForm((prev) => ({ ...prev, branch_id: "" }));
      }
    }

    fetchBranches();
    fetchData(selectedCompany);
  }, [selectedCompany]);

  const fetchData = async (companyId) => {
    if (!companyId) {
      setPackages([]);
      setBranches([]);
      setServices([]);
      return;
    }
    setLoading(true);
    try {
      const [pkgRes, branchRes, svcRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/companies/${companyId}/packages`),
        axios.get(`${API_BASE_URL}/companies/${companyId}/branches`),
        axios.get(`${API_BASE_URL}/companies/${companyId}/services`),
      ]);
      setPackages(pkgRes.data || []);
      setBranches(branchRes.data || []);
      setServices(svcRes.data || []);
    } catch (err) {
      console.error("fetchData", err);
      Swal.fire("Error", "Failed to load packages", "error");
      setPackages([]);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const id = e.target.value;
    setSelectedCompany(id);
    setForm((prev) => ({ ...prev, branch_id: "" }));
    localStorage.removeItem("selectedBranch");
    const selectedCompanyObj = companies.find((c) => c.id === id);
    localStorage.setItem("selectedCompany", JSON.stringify(selectedCompanyObj || {}));
    if (id) fetchData(id);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "branch_id") {
      const selectedBranch = branches.find((b) => b.id === value);
      localStorage.setItem("selectedBranch", JSON.stringify(selectedBranch || {}));
    }
  };

  const toggleService = (id) => {
    setForm((prev) => {
      const included = prev.included_services.includes(id)
        ? prev.included_services.filter((s) => s !== id)
        : [...prev.included_services, id];
      return { ...prev, included_services: included };
    });
  };

  const handleSave = async () => {
    if (!selectedCompany) return Swal.fire("Select a company", "", "warning");
    if (!form.name) return Swal.fire("Package name required", "", "warning");
    try {
      await axios.post(`${API_BASE_URL}/companies/${selectedCompany}/packages`, form);
      Swal.fire("Success", "Package saved successfully", "success");
      setShowModal(false);
      setForm({
        name: "",
        price_cents: "",
        branch_id: form.branch_id,
        included_services: [],
      });
      fetchData(selectedCompany);
    } catch (err) {
      console.error("handleSave", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to save package", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Package?",
      text: "This will remove all associated services.",
      icon: "warning",
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/packages/${id}`);
      Swal.fire("Deleted", "Package removed successfully", "success");
      fetchData(selectedCompany);
    } catch (err) {
      console.error("handleDelete", err);
      Swal.fire("Error", "Failed to delete package", "error");
    }
  };

  return (
    <div className="container mt-4">
      <h3>
        <i className="bi bi-box me-2"></i>Manage Packages
      </h3>

      <Row className="align-items-end mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Select Company</Form.Label>
            <Form.Select value={selectedCompany} onChange={handleCompanyChange}>
              <option value="">— Select —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md="auto">
          <Button
            variant="primary"
            onClick={() => setShowModal(true)}
            disabled={!selectedCompany}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Package
          </Button>
        </Col>
      </Row>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Price</th>
              <th>Branch</th>
              <th>Included Services</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {packages.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  No packages found
                </td>
              </tr>
            ) : (
              packages.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>₹{p.price_cents}</td>
                  <td>{p.branch_name || "All"}</td>
                  <td>{(p.services || []).map((s) => s.name).join(", ")}</td>
                  <td>
                    <Button
                      variant="link"
                      className="text-danger p-1"
                      onClick={() => handleDelete(p.id)}
                      title="Delete Package"
                    >
                      <i className="bi bi-trash fs-5"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Package</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Package Name</Form.Label>
              <Form.Control name="name" value={form.name} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Price (₹)</Form.Label>
              <Form.Control
                type="number"
                name="price_cents"
                value={form.price_cents}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Branch (optional)</Form.Label>
              <Form.Select name="branch_id" value={form.branch_id} onChange={handleFormChange}>
                <option value="">All Branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Included Services</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {services.map((s) => (
                  <Form.Check
                    inline
                    key={s.id}
                    type="checkbox"
                    label={s.name}
                    checked={form.included_services.includes(s.id)}
                    onChange={() => toggleService(s.id)}
                  />
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}